import { TFunction } from 'i18next'
import { isEmpty, isObject } from 'lodash/fp'

import { Attribute, EntityItem } from 'api/ApiTypes'
import { getCountryNameByCode } from 'components/my-account/country-selector/countries'
import AttributeUtils, { COMBINED_ATTRIBUTES } from 'utils/AttributeUtils'
import CommonUtils, { formatAddress } from 'utils/CommonUtils'
import { convertToLocaleNumber } from 'utils/LanguageUtils'
import { Address } from 'utils/OrderUtils'
import { formatBillingAmount, formatIban } from 'utils/PaymentUtils'

import { isEmptyValue } from './getter'

export const EMPTY_VALUE = '-'

export const formatDate = (value: string, emptyValue?: string) =>
  CommonUtils.toLocaleDate(value, emptyValue)

export const formatDateTime = (value: string, emptyValue?: string) =>
  CommonUtils.toLocaleDateAndTime(value, emptyValue)

export const formatRelation = (
  value: any,
  relations: EntityItem[],
  emptyValue = EMPTY_VALUE
) => {
  if (value?.$relation) {
    const relationValue: any = AttributeUtils.findValueFromRelations(
      value,
      relations
    )

    return relationValue?.length ? relationValue : emptyValue
  }

  return emptyValue
}

export const formatRelationPaymentMethod = (
  value: any,
  t: TFunction,
  emptyValue = EMPTY_VALUE
) => {
  const data = value?.data
  const type = value?.type

  if (!type) {
    return emptyValue
  }

  const paymentType = t(`payment.${type}`)

  // If no data or empty data, just return the payment type
  if (!data || isEmpty(data)) {
    return paymentType
  }

  // If we have data, format with details
  const ibanNumber = data?.iban ? `\n${formatIban(data?.iban)}` : ''
  const fullName = data?.fullname ? `\n${data.fullname}` : ''
  const bankName = data?.bank_name ? `\n${data.bank_name}` : ''
  const bicNumber = data?.bic_number ? `\n${data.bic_number}` : ''

  return `${paymentType}${fullName}${bankName}${bicNumber}${ibanNumber}`
}

export const formatAddressValue = (
  value: Address[],
  emptyValue = EMPTY_VALUE
) => (value?.length ? formatAddress(value[0]) : emptyValue)

export const formatRelationAddress = (value: any, emptyValue = EMPTY_VALUE) => {
  const address = value

  if (!address || isEmpty(address)) {
    return emptyValue
  }

  const country = getCountryNameByCode(address.country) || ''

  return formatAddress({ ...address, country })
}

export const formatCurrency = (
  value: any,
  entity: EntityItem,
  attribute: Attribute,
  emptyValue = EMPTY_VALUE
) => {
  if (isEmptyValue(value)) {
    return emptyValue
  }

  const currency = entity?.[`${attribute.name}_currency`] || entity?.currency

  // TODO: Remove this once we drop support for old portals
  if (COMBINED_ATTRIBUTES.has(attribute.name) && entity) {
    return CommonUtils.toLocalePrice(value, currency) || value
  }

  const currencyValue = currency
    ? CommonUtils.toLocalePrice(value, currency)
    : entity[`${attribute.name}_decimal`] || value

  // TODO: Remove use of formatBillingAmount once we drop support for old portals
  return formatBillingAmount(String(currencyValue))
}

export const formatNumber = (
  value: number,
  entity: EntityItem,
  attribute: Attribute,
  t: TFunction,
  emptyValue = EMPTY_VALUE
) => {
  if (isEmptyValue(value)) {
    return emptyValue
  }

  const formattedValue = COMBINED_ATTRIBUTES.has(attribute.name)
    ? AttributeUtils.getAttributeValueByType(entity, attribute, t)
    : convertToLocaleNumber(value)

  return formattedValue || emptyValue
}

export const formatString = (
  value: string | null | undefined,
  emptyValue = EMPTY_VALUE
) => value ?? emptyValue

export const formatDefault = (value: any, emptyValue = EMPTY_VALUE) => {
  const isPrimitive = !isObject(value)

  return isPrimitive ? value : emptyValue
}

export const formatStatus = (
  value: any,
  t: TFunction,
  emptyValue = EMPTY_VALUE,
  fallbackLabel?: string
) => {
  if (isEmptyValue(value)) {
    return emptyValue
  }

  // Prefer a canonical `status_label.*` translation. When the value isn't a
  // known canonical status (e.g. an org-custom status or a workflow-derived
  // value like "Done"/"Anfrage"), fall back to the schema option's own label
  // when available, otherwise the raw value.
  return t(`status_label.${value}`, fallbackLabel ?? value)
}

export const formatBoolean = (
  value: any,
  t: TFunction,
  emptyValue = EMPTY_VALUE
) => {
  if (isEmptyValue(value)) {
    return emptyValue
  }

  return value ? t('common.yes') : t('common.no')
}
